import React, {
    useContext, useCallback, useState, useEffect
} from 'react';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { GatewayDocumentService } from 'gw-capability-gateway-document';
import { ClausesUtil } from 'gw-policycommon-util-js';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
import { MockUpUtil } from 'gw-portals-util-js';
import {SubmissionService} from 'gw-capability-gateway';
import { ModalNextProvider, Loader, DropdownSelectField } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import messages from './QuotePage.messages';
import styles from './QuotePage.module.scss';

import metadata from './QuotePage.metadata.json5';
import SimpleQuoteClauseTable from '../../components/SimpleQuoteClauseTable/SimpleQuoteClauseTable';

function generateClauseData(columnData, coveragePath, pcvPublicId = undefined) {
    if (!pcvPublicId) {
        return columnData.map(({ lob, code }) => {
            const completeCoveragePath = coveragePath === 'packageSpecificCoverages' ?
                'coverages.dwellingCoverages[0].additionalCoverages' : `coverages.${coveragePath}`;
            return {
                code: code,
                path: `${lob.path}.${completeCoveragePath}`,
                clauses: _.get(lob.data, completeCoveragePath.replace(/\.children/, ''))
            };
        });
    } else {
        return columnData.map(({ lob, code }) => {
            const p = lob.data.coverages.dwellingCoverages.findIndex(d => d.publicID == pcvPublicId);
            const completeCoveragePath = `coverages.dwellingCoverages[${p}].additionalCoverages`;
            return {
                code: code,
                path: `${lob.path}.${completeCoveragePath}`,
                clauses: _.get(lob.data, completeCoveragePath.replace(/\.children/, ''))
            };
        });
    }
}


const structureCustomQuote = (submissionVM, affectedQuote, clauses) => {
    // convert OfferingDTO to CustomQuotedDTO structure
    return {
        quote: affectedQuote,
        quoteID: submissionVM.quoteID.value,
        sessionUUID: submissionVM.sessionUUID.value,
        periodStart: submissionVM.baseData.periodStartDate.value,
        periodEnd: submissionVM.baseData.periodEndDate.value,
        coverages: clauses,
        multiLineDiscount:submissionVM.baseData.multiLineDiscount.value,
    };
};

const getCustomQuote = (vm, lobPath, quotePath, lobName, filterChangedClauses = false) => {
    const lobOffering = _.get(vm, `${lobPath}.value`);
    const quoteOffering = _.get(vm, `${quotePath}.value`);

    let clausesToUpdate = {
        [lobName]: lobOffering.coverages
    };

    if (filterChangedClauses) {
        clausesToUpdate = ClausesUtil.structureClausesForServer(
            lobOffering.coverages,
            lobName,
            null
        );
    }

    return structureCustomQuote(vm, quoteOffering, clausesToUpdate);
};

const generateColumnData = (submissionVM) => {
    const lobOfferingPath = 'lobData.homeowners.offerings';
    const quoteOfferingPath = 'quoteData.offeredQuotes';

    const lobOfferings = _.get(submissionVM, `${lobOfferingPath}.value`);
    const quoteOfferings = _.get(submissionVM, `${quoteOfferingPath}.value`);

    const columnData = lobOfferings.map((lobOffering, lobIndex) => {
        const quoteDataIndex = quoteOfferings.findIndex(
            (qdOffering) => qdOffering.branchCode === lobOffering.branchCode
        );
        const quoteData = quoteOfferings[quoteDataIndex];
        return {
            name: lobOffering.branchName,
            code: lobOffering.branchCode,
            quote: {
                path: `${quoteOfferingPath}.children.${quoteDataIndex}`,
                data: quoteData
            },
            lob: {
                path: `${lobOfferingPath}.children.${lobIndex}`,
                data: lobOffering
            }
        };
    });
    return _.sortBy(columnData, ['code']);
};

const getCoveragesUniqueID = (submissionVM) => {
    // putting ID into an object as the Jutro table component expects an object
    const structureClauseTableData = (coverage) => ({ coverageUniqueID: coverage.publicID });

    const isLiabilityLinePresent = submissionVM.baseData.isFarmLineExist.value ? submissionVM.lobData.farm.offerings.value[0].coverages.isLiabilityLinePresent : submissionVM.lobData.homeowners.offerings.value[0].coverages.isLiabilityLinePresent ;

    const offerings = _.get(submissionVM, 'lobData.homeowners.offerings.value');
    
    const liablityLineCoverages = isLiabilityLinePresent ? _.uniqBy(
        offerings.flatMap((offering) => offering.coverages.liablityLineCoverages ? offering.coverages.liablityLineCoverages.map(structureClauseTableData) : []),
        'coverageUniqueID'
    ) : [];
    // const baseCoverages = _.uniqBy(
    //     offerings.flatMap((offering) => offering.coverages
    //         .baseCoverages.map(structureClauseTableData)),
    //     'coverageUniqueID'
    // );
    // const additionalCoverages = _.uniqBy(
    //     offerings.flatMap((offering) => offering.coverages.additionalCoverages?
    //     offering.coverages.additionalCoverages.map(structureClauseTableData): []),
    //     'coverageUniqueID'
    // );

    //~ Package level coverages
    const packageSpecificCoverages = _.unionBy(offerings.flatMap((offering) =>
        offering.coverages.dwellingCoverages.flatMap(dwe => dwe.additionalCoverages)
        .map(structureClauseTableData)),
        'coverageUniqueID'
    );


     const packageSpecificCoverages_1 =  offerings.flatMap((offering) => offering.coverages.dwellingCoverages.map(dwe =>{ return { 'name': dwe.dwelingName, 'publicID':dwe.publicID,  'data': dwe.additionalCoverages.map(structureClauseTableData)}} ))


    return {
      packageSpecificCoverages,
    //   baseCoverages,
    //   additionalCoverages,
      liablityLineCoverages,
      packageSpecificCoverages_1
    };
};



const generateTableData = (submissionVM, columnData) => {

    const uniqueID = getCoveragesUniqueID(submissionVM);

    const lineCoverages = {
        header: "Liability Line Coverages", //translator(messages.generalCoverages),
        data: _.get(uniqueID, "liablityLineCoverages"),
        tableContent: generateClauseData(columnData, 'liablityLineCoverages')
    };

    const v = _.get(uniqueID, "packageSpecificCoverages_1");
    const pcv = v.map(e => {
        return {
            header: "Package-Specific Coverages - " + e.name,
            data: e.data,
            tableContent: generateClauseData(columnData, 'packageSpecificCoverages', e.publicID)
        }
    })


    // if(Object.keys(uniqueID) === undefined) return null;
    // return uniqueID === undefined ? null: Object.keys(uniqueID).map((coverageType) => ({
    //     header: _.get(messages, coverageType),
    //     data: _.get(uniqueID, coverageType),
    //     tableContent: generateClauseData(columnData, coverageType)
    // }));

    return [ ...pcv, lineCoverages]
};


const showQuoteStartDate = appConfig.showQuoteStartDateInHeader;

function QuotePage(props) {
    const [staleQuoteBranchCode, setStaleQuoteBranchCode] = useState(undefined);
    const breakpoint = useContext(BreakpointTrackerContext);
    const { CustomQuoteService } = useDependencies('CustomQuoteService');
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { authHeader, checkPermission } = useAuthentication();
    const { isComponentValid, onValidate, disregardFieldValidation } = useValidation('QuotePage');
    const viewModelService = useContext(ViewModelServiceContext);
    const DocumentPageService = {
        downloadDocument: GatewayDocumentService.downloadDocument
    };
    const translator = useContext(TranslatorContext);
    const {
        wizardSnapshot,
        wizardData: submissionVM,
        updateWizardData,
        underwritingIssues,
        quoteData,
        updateQuotedData
    } = props;
    const [initialSubmissionVM, setInitialSubmissionVM] = useState(undefined);
    const [extensions, setExtensions] = useState([]);
    const [selectedExtensions, setSelectedExtensions] = useState([]);
    const [searchExtension, setSearchExtension] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [isPageInitialized, setPageInitialized] = useState(false);

    useEffect(() => {
        if(!isPageInitialized){
            // inject mock contactPhone to prevent wizardData.subtreeValid is invalid
            submissionVM.value = MockUpUtil.setMockData(
                submissionVM.value,
                'quote.ho',
                'bindData.contactPhone'
            );

            setInitialSubmissionVM(viewModelService.clone(wizardSnapshot));

            LoadSaveService.getLiabilityExtensions(
                submissionVM.quoteID.value,
                false,
                authHeader
            ).then((liabilityExtensions) => {
                if(Array.isArray(liabilityExtensions)){
                    const _extensions = liabilityExtensions.map((c) => {
                        c['id'] = c.publicID;
                        return c
                    });
                    setExtensions(_extensions);
                }               
            });
            // Store the initial SubmissionVM when component is mounted
            // eslint-disable-next-line react-hooks/exhaustive-deps
            setPageInitialized(true);
        }
    }, [submissionVM]);

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            updateWizardData(submissionVM);
        },
        [submissionVM, updateWizardData]
    );

    const getNewSubmissionVM = useCallback(
        (response, lobPath, quotePath, lobName) => {
            const clonedSubmissionVM = viewModelService.clone(submissionVM);
            const updatedClauses = _.get(response, `coverages.${lobName}`);
            // Update local offering with new one from xcenter
            _.set(clonedSubmissionVM, `${lobPath}.coverages`, updatedClauses);
            // Update local quote with new one from xcenter
            const status = _.get(response, 'quote.status', 'Draft');
            _.set(clonedSubmissionVM, `${quotePath}.status`, status);
            // Update premium with new one from xcenter
            _.set(clonedSubmissionVM, `${quotePath}.premium`, response.quote.premium);
            // Update local errorsAndWarnings with new one from xcenter
            _.set(clonedSubmissionVM, 'errorsAndWarnings', response.errorsAndWarnings);
            return clonedSubmissionVM;
        },
        [viewModelService, submissionVM]
    );

    const onUpdateCustomQuote = useCallback(
        (_basePath, lobPath, quotePath) => {
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, quotePath, lobName, true);
            return CustomQuoteService.updateCustomQuoteCoverages(customQuote, authHeader).then(
                (response) => {
                    const newSubmissionVM = getNewSubmissionVM(
                        response,
                        lobPath,
                        quotePath,
                        lobName
                    );
                    const removedFieldsFromBaseCoverages = ClausesUtil.getRemovedClausesID(
                        submissionVM,
                        newSubmissionVM,
                        `${lobPath}.coverages.baseCoverages`
                    );
                    // const removedFieldsFromBaseCoverages = ClausesUtil.getRemovedClausesID(
                    //     submissionVM,
                    //     newSubmissionVM,
                    //     `${lobPath}.coverages.liablityLineCoverages`
                    // );
                    const removedFieldsFromAdditionalCoverages = ClausesUtil.getRemovedClausesID(
                        submissionVM,
                        newSubmissionVM,
                        `${lobPath}.coverages.dwellingCoverages.children[0].additionalCoverages`
                    );
                    const allRemovedFields = [
                        ...removedFieldsFromBaseCoverages,
                        ...removedFieldsFromAdditionalCoverages
                    ];
                    disregardFieldValidation(allRemovedFields);
                    updateWizardData(newSubmissionVM);
                }
            );
        },
        [
            submissionVM,
            CustomQuoteService,
            authHeader,
            getNewSubmissionVM,
            disregardFieldValidation,
            updateWizardData
        ]
    );

    const syncCoverages = useCallback(
        (value, changedPath, lobPath, quotePath) => {
            const basePath = ClausesUtil.getObjectPathFromChangedPath(changedPath);
            return onUpdateCustomQuote(basePath, lobPath, quotePath);
        },
        [onUpdateCustomQuote]
    );

    const onClauseChange = useCallback(
        (index, schedule, path) => {
            const lobOfferingPath = 'lobData.homeowners.offerings.children[0]';
            writeValue(schedule, path);
            return onUpdateCustomQuote({}, lobOfferingPath);
        },
        [onUpdateCustomQuote, writeValue]
    );


    const handlePrint = () => {
        window.print();
    };

    const onScheduleChange = useCallback(
        (schedule, path) => {
            const lobOfferingPath = 'lobData.homeowners.offerings.children[0]';
            const quoteOfferingPath = 'quoteData.offeredQuotes.children[0]';
            writeValue(schedule, `${path}.value`);
            const offering = _.get(submissionVM, `${lobOfferingPath}.value`);
            setStaleQuoteBranchCode(offering.branchCode);
            return onUpdateCustomQuote({}, lobOfferingPath, quoteOfferingPath);
        },
        [onUpdateCustomQuote, submissionVM, writeValue]
    );

    const changeSubmission = useCallback(
        (value, changedPath) => {
            updateWizardData(ClausesUtil.setClauseValue(submissionVM, value, changedPath));
        },
        [submissionVM, updateWizardData]
    );

    const changeSubmissionAndSync = useCallback(
        (value, changedPath, lobPath, quotePath) => {
            changeSubmission(value, changedPath);
            return syncCoverages(value, changedPath, lobPath, quotePath);
        },
        [changeSubmission, syncCoverages]
    );

    const resetQuote = useCallback(
        (lobPath, quotePath) => {
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(initialSubmissionVM, lobPath, quotePath, lobName);

            return CustomQuoteService.forceUpdateCustomQuoteCoverages(customQuote, authHeader).then(
                (response) => {
                    const newSubmissionVM = getNewSubmissionVM(
                        response,
                        lobPath,
                        quotePath,
                        lobName
                    );
                    updateWizardData(newSubmissionVM);
                }
            );
        },
        [initialSubmissionVM, CustomQuoteService, authHeader, getNewSubmissionVM, updateWizardData]
    );

    const recalculate = useCallback(
        (lobPath, quotePath) => {
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, quotePath, lobName);

            return CustomQuoteService.updateCustomQuote(customQuote, authHeader).then(
                (response) => {
                    setStaleQuoteBranchCode(undefined);
                    const newSubmissionVM = getNewSubmissionVM(
                        response,
                        lobPath,
                        quotePath,
                        lobName
                    );
                    updateWizardData(newSubmissionVM);
                    return response;
                }
            );
        },
        [submissionVM, CustomQuoteService, authHeader, getNewSubmissionVM, updateWizardData]
    );

    const generateQuoteDocument = useCallback(
        () => {
        ModalNextProvider.showConfirm({
              title: messages.farmGenerateQuoteDocument,
              message: messages.farmGenerateQuoteDocumentConf,
              status: 'warning',
              icon: 'mi-error-outline'
          }).then((results) => {
            if (results === 'cancel') {
                return _.noop();
            }
            setLoading(true);
            SubmissionService.generateQuoteDocumentPortal(submissionVM.value, !!submissionVM.baseData.value.isIssuanceJob, authHeader).then((response) => {
                setLoading(false);
                if(response.publicID && response.sessionID){
                    var downloadUrl = DocumentPageService.downloadDocument(
                        response.publicID,
                        response.sessionID
                    );
                    window.open(downloadUrl, '_blank');
                } 
                else{
                    ModalNextProvider.showAlert({
                        title: 'Unable to generate quote document',
                        message: 'Problem occured while generating quote document. Please try again later',
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: 'Ok'
                    }).catch(_.noop);
                }                
            }).catch(() => {
                setLoading(false);
                ModalNextProvider.showAlert({
                    title: 'Unable to generate quote document',
                    message: 'Problem occured while generating quote document. Please try again later',
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: 'Ok'
                }).catch(_.noop);
            });
        }, _.noop);
        },
        [submissionVM, authHeader]
    );

    const buyNow = useCallback(
        async () => {
            /*const lobOffering = _.get(submissionVM, `${lobPath}.value`);
            const quoteOffering = _.get(submissionVM, `${quotePath}.value`);
            const quoteID = _.get(submissionVM, 'quoteID.value');
            const sessionUUID = _.get(submissionVM, 'sessionUUID.value');
            await CustomQuoteService.setSelectedVersionOnSubmission(
                quoteID,
                lobOffering.branchName,
                sessionUUID,
                authHeader
            ).then(() => LoadSaveService.updateQuotedSubmission(submissionVM.value, authHeader));


            // set chosen quote
            _.set(submissionVM, 'bindData.chosenQuote.value', quoteOffering.publicID);
            updateWizardData(submissionVM);*/
        },
        [submissionVM, updateWizardData, CustomQuoteService,LoadSaveService, authHeader]
    );

    //const columnData = generateColumnData(submissionVM);

    //~ Extension code
    const updateExtensionValue = (value, path, index) => {
        writeValue(value, `lobData.homeowners.offerings.children.0.coverages.liabilityExtensions.children.${index}.extensionValue`);
    }

    const generateExtensionOverrides = () => {
        const overrides = submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.children.map((item, index) => {
            return {
                [`extensionLiabilityTitle${index}`]: {
                    content: `${item['name'].value}`,
                },
                [`extenionName${index}`]: {
                    "label": `${item['description'].value}`,
                    "value": translator({id: item['descriptionValue'].value.name})
                },
                [`extensionValue${index}`]: {
                    "label": `${item['valueDescription'].value}`,
                    onValueChange: (value, path) => { updateExtensionValue(value, path, index) }
                },
                [`addExtensionLocation${index}`]:{
                    onClick: () => {addExtensionLocation(item, index) }
                },
                [`extensionLocationContainer${index}`]:{
                    visible: item.accountLocationPublicId.value.length > 0,
                    data: item.accountLocationPublicId.value
                },
                [`deleteLocation${index}`]:{
                    onClick: (value, deleteIndex) => { deleteExtensionLocation(deleteIndex, item, index) }
                },
                [`extensionLocation${index}`]:{
                    onCell: (item, _index, props) => { return getDataTableCell(item, _index, props, index)}
                },
                [`deleteExtensionButton${index}`]:{
                    onClick: () => { deleteExtension(index) }
                }
            };
        });
        return Object.assign({}, ...overrides);
    }

    const generateScheduleChangeHeadingOverride = () => {
        const overrides = submissionVM.lobData.homeowners.offerings.children[0].coverages.dwellingCoverages.children.map((item, index) => {
            return {
                [`schedules${index}`]: {
                    content: `Schedule Items - ${item.dwelingName.value}`,
                },

            };
        });
        return Object.assign({}, ...overrides);
    }



    const onSelectionChange = (selectedIds) => {
        const _selectedExtensions = _.filter(extensions, (e) => { return selectedIds.indexOf(e.id) != -1 })
        setSelectedExtensions(_selectedExtensions);
        console.log(_selectedExtensions);
    }

    const addExtensions = () => {
        LoadSaveService.addLiabilityExtenstionForProduct(
            submissionVM.value, selectedExtensions,
            authHeader
        ).then((response) => {
            setSearchExtension("");
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM, `lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.value`, response.lobData.homeowners.offerings[0].coverages.liabilityExtensions);
            updateWizardData(newSubmissionVM);
        });
    };
    const deleteExtension = (index) => {
        const deleteExtension = _.get(submissionVM, `lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.value.${index}`);
        LoadSaveService.removeLiabilityExtensionForProduct(
            submissionVM.value, deleteExtension,
            authHeader
        ).then((response) => {
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM, `lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.value`, response.lobData.homeowners.offerings[0].coverages.liabilityExtensions);
            updateWizardData(newSubmissionVM);
        });
    }

    const addExtensionLocation = (item, index) => {
        item.accountLocationPublicId.value.push("");
        updateWizardData(submissionVM);
    }

    const deleteExtensionLocation = (deleteIndex, item, index) => {
        item.accountLocationPublicId.value.splice(deleteIndex, 1);
        updateWizardData(submissionVM);
    }

    const updateExtensionLocation = (value, index, locationIndex) => {
        submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.children[locationIndex].accountLocationPublicId.children[index].value = value;
        updateWizardData(submissionVM);
    }

    const handleSearchExtension = (value) => {
        setSearchExtension(value);
    }

    const getDataTableCell = (item, index, props, locationIndex) => {
        const path = props.path;
        const value = item;
        const availableLocations = submissionVM.baseData.policyLocations.value.map( (m) => { m['id'] = m.publicID; return m } );
        return (
            <DropdownSelectField
                className={styles.locationDropdown}
                id={index + path}
                onValueChange={(value, path) => { updateExtensionLocation(value, index, locationIndex) }}
                model={item}
                value={value}
                availableValues={availableLocations}
            />
        )

    };

    const changeMultilineDiscount = useCallback(
        (value, changedPath) => {
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.set(newSubmissionVM.value, 'baseData.multiLineDiscount', value);
            updateWizardData(newSubmissionVM);
            /*_.set(submissionVM, `baseData.${changedPath}`, value);
            updateWizardData(submissionVM);*/
        },
        [submissionVM, updateWizardData]
    );


    const overrideProps = {
        '@field': {
            // apply to all fields
            showOptional: false,
            showRequired: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
        },


        quoteTable: {
            columnData: [],
            tableData: [],
            underwritingIssues: underwritingIssues,
            quoteID: submissionVM.quoteID.value,
            filterUWIssuesInCustomOffering: false,
            baseData: submissionVM.baseData.value,
            bindData: submissionVM.bindData.value,
            isQuoted: quoteData.isQuoted,
            updateQuotedData: (value) => {
                updateQuotedData(value);
                updateWizardData(submissionVM);
            },
            submissionVM: submissionVM
        },

        startDate: {
            visible: showQuoteStartDate
        },
        printPage: {
            visible: checkPermission('genquotesummarydoc_EY') && !showQuoteStartDate
        },
        /*extensionsDataTable: {
            data: searchExtension.trim() ? extensions.filter((d) => { return d.name.toLowerCase().indexOf(searchExtension.trim().toLowerCase()) != -1   }) : extensions,
            onSelectionChange: onSelectionChange
        },
        extensionHeader:{
            visible: submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions
            ? submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.children.length > 0
            : false
        },
        addExtension:{
            onClick: addExtensions
        },
        searchExtensionInput:{
            value:searchExtension
        },
        ...generateExtensionOverrides(),
        ...generateScheduleChangeHeadingOverride()*/
    };

    const onStaleQuoteBranchCode = useCallback(() => {
        return staleQuoteBranchCode;
    }, [staleQuoteBranchCode]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    return (
        <WizardPage
            showNext={false}
            showCancel={false}
            showPrevious={false}
            disableNext={!isComponentValid}
        >
            {({ onNext }) => {
                const resolvers = {
                    resolveClassNameMap: styles,
                    resolveCallbackMap: {
                        onBuyNow: () => buyNow().then(onNext),
                        onRecalculate: recalculate,
                        onResetQuote: resetQuote,
                        onChangeSubmissionAndSync: changeSubmissionAndSync,
                        onChangeSubmission: changeSubmission,
                        onSyncCoverages: syncCoverages,
                        onScheduleChange: onScheduleChange,
                        onStaleQuoteBranchCode: onStaleQuoteBranchCode,
                        onPrint: handlePrint,
                        getDataTableCell: getDataTableCell,
                        deleteExtensionLocation: deleteExtensionLocation,
                        handleSearchExtension: handleSearchExtension,
                        onChangeMultilineDiscount: changeMultilineDiscount,
                        generateQuoteDocument: generateQuoteDocument
                    },
                    resolveComponentMap: {
                        simpleQuoteClauseTable : SimpleQuoteClauseTable
                    },
                };
                return (
                    <ViewModelForm
                        uiProps={metadata.pageContent}
                        model={submissionVM}
                        overrideProps={overrideProps}
                        onModelChange={updateWizardData}
                        onValidationChange={onValidate}
                        classNameMap={resolvers.resolveClassNameMap}
                        callbackMap={resolvers.resolveCallbackMap}
                        componentMap={resolvers.resolveComponentMap}
                    />
                );
            }}
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
